import React, { useState, useEffect, useRef } from 'react';
import { API } from '../App';

// The three lists an observer picks from when starting a test: this casino's pits,
// its table minimums, and its types of test. Each casino keeps its own — one
// property's pits mean nothing at another.
//
// A test stores the label it was given, so retiring a choice here never changes a
// test that already used it.

const FLOOR_LISTS = [
  {
    kind: 'pit',
    title: 'Pits',
    blurb: 'The pits an observer can choose from. Stored on every test, so you can compare pits later.',
    placeholder: 'e.g. Pit 3',
  },
  {
    kind: 'table_min',
    title: 'Table minimums',
    blurb: 'The minimum bets your tables run. A $100 table deals differently from a $10 table, and this is what lets a report show that.',
    placeholder: 'e.g. $25',
  },
  {
    kind: 'test_type',
    title: 'Types of test',
    blurb: 'Why a test was run — routine, new hire, follow-up after coaching. This is separate from Benchmark and Standard, which is set by the game’s mode, not by the observer.',
    placeholder: 'e.g. Follow-up after coaching',
  },
];

const BLACKJACK_LISTS = [
  {
    kind: 'shuffle_type',
    title: 'Shuffle type',
    blurb: 'How the cards get shuffled at that table — a machine, or by hand. Shuffle times are benchmarked separately for each, so a machine is only ever measured against a machine.',
    placeholder: 'e.g. Auto shuffler',
  },
  {
    kind: 'deck_count',
    title: 'Number of decks',
    blurb: 'How many decks are in play. A six-deck hand shuffle is not a two-deck hand shuffle, so this splits the shuffle benchmark further.',
    placeholder: 'e.g. 6 decks',
  },
];

const BACCARAT_LISTS = [
  {
    kind: 'game_style',
    title: 'Game style',
    blurb: 'Macau, standard, midi, mini. The cards are drawn differently, so dealer draw time is benchmarked separately for each style.',
    placeholder: 'e.g. Macau',
  },
  {
    kind: 'shuffle_type',
    title: 'Shuffle type',
    blurb: 'How that table shuffles. Shuffle times are benchmarked separately for each, so a machine is only ever measured against a machine.',
    placeholder: 'e.g. Batch shuffler',
  },
  {
    kind: 'side_bet_type',
    title: 'Side bet',
    blurb: 'Which side bet the table offers. Recorded on every test, so side bet pay times can be compared like for like.',
    placeholder: 'e.g. Dragon Bonus',
  },
];

const GROUPS = {
  floor: { lists: FLOOR_LISTS, gameKind: null, title: 'Floor Setup' },
  blackjack: { lists: BLACKJACK_LISTS, gameKind: 'blackjack', title: 'Blackjack Variables' },
  baccarat: { lists: BACCARAT_LISTS, gameKind: 'baccarat', title: 'Baccarat Variables' },
};

const styles = {
  card: { backgroundColor: '#16213e', borderRadius: '16px', padding: '24px', marginBottom: '24px' },
  cardTitle: { color: '#ffffff', fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' },
  cardSubtitle: { color: '#a0a0b0', fontSize: '13px', marginBottom: '20px', lineHeight: 1.6, maxWidth: '760px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' },
  panel: { backgroundColor: '#0f3460', borderRadius: '12px', padding: '18px' },
  panelTitle: { color: '#ffffff', fontSize: '15px', fontWeight: 'bold', marginBottom: '4px' },
  panelBlurb: { color: '#a0a0b0', fontSize: '12px', lineHeight: 1.6, marginBottom: '14px' },
  row: {
    display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
    backgroundColor: '#16213e', borderRadius: '8px', marginBottom: '6px',
  },
  rowLabel: { color: '#ffffff', fontSize: '14px', flex: 1 },
  rowLabelOff: { color: '#6e7891', fontSize: '14px', flex: 1, textDecoration: 'line-through' },
  iconBtn: {
    width: '26px', height: '26px', padding: 0, backgroundColor: 'transparent',
    border: '1px solid #2a4a7a', borderRadius: '6px', color: '#a0a0b0',
    fontSize: '12px', cursor: 'pointer', lineHeight: 1,
  },
  textBtn: {
    padding: '4px 10px', backgroundColor: 'transparent', border: '1px solid #2a4a7a',
    borderRadius: '6px', color: '#a0a0b0', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  addRow: { display: 'flex', gap: '8px', marginTop: '12px' },
  input: {
    flex: 1, minWidth: 0, padding: '9px 11px', backgroundColor: '#16213e',
    border: '1px solid #2a4a7a', borderRadius: '8px', color: '#ffffff', fontSize: '14px', outline: 'none',
  },
  addBtn: {
    padding: '9px 16px', backgroundColor: '#e94560', border: 'none', borderRadius: '8px',
    color: '#ffffff', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer',
  },
  empty: { color: '#6e7891', fontSize: '13px', fontStyle: 'italic', padding: '6px 0 2px' },
  error: { color: '#e94560', fontSize: '12px', marginTop: '8px' },
  note: { color: '#a0a0b0', fontSize: '12px', marginTop: '16px', lineHeight: 1.6 },
};

function FloorOptions({ isAdmin, group = 'floor' }) {
  const { lists, gameKind, title } = GROUPS[group] || GROUPS.floor;
  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };
  const [options, setOptions] = useState({});
  const [drafts, setDrafts] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  // Adding a list is usually adding several, so the cursor stays where it was.
  const inputs = useRef({});

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    try {
      const res = await fetch(`${API}/admin/floor-options`, { headers });
      const d = await res.json();
      // Each list belongs to one game, or to every game when it has none.
      const mine = {};
      Object.entries(d || {}).forEach(([kind, rows]) => {
        mine[kind] = (rows || []).filter(o => (o.game_kind || null) === gameKind);
      });
      setOptions(mine);
    } catch (e) {
      setErrors({ load: 'Could not load these lists' });
    }
  }

  async function add(kind) {
    const label = (drafts[kind] || '').trim();
    if (!label) return;
    setBusy(true);
    setErrors(prev => ({ ...prev, [kind]: '' }));
    try {
      const res = await fetch(`${API}/admin/floor-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ kind, label, game_kind: gameKind }),
      });
      const d = await res.json();
      if (res.ok) {
        setDrafts(prev => ({ ...prev, [kind]: '' }));
        await load();
        inputs.current[kind]?.focus();
      } else {
        setErrors(prev => ({ ...prev, [kind]: typeof d.detail === 'string' ? d.detail : 'Could not add that' }));
      }
    } catch (e) {
      setErrors(prev => ({ ...prev, [kind]: 'Could not connect to server' }));
    }
    setBusy(false);
  }

  async function change(id, patch) {
    setBusy(true);
    try {
      await fetch(`${API}/admin/floor-options/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(patch),
      });
      await load();
    } catch (e) {
      setErrors(prev => ({ ...prev, load: 'Could not connect to server' }));
    }
    setBusy(false);
  }

  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>{title}</div>
      <div style={styles.cardSubtitle}>
        {gameKind ? (
          <>Asked at the top of the screen on {title.split(' ')[0]} tests, and recorded on every one. Unlike the
          lists above, these change what a dealer is measured against — a shuffle is compared with the same
          kind of shuffle, a Macau draw with a Macau draw. Leave a list empty and the question is left off the
          floor app.</>
        ) : (
          <>What an observer picks from when starting a test, on every game. Leave a list empty and that question
          is left off the floor app altogether. The order here is the order on the phone, so put the common
          answers at the top.</>
        )}
      </div>

      <div style={styles.grid}>
        {lists.map(({ kind, title, blurb, placeholder }) => {
          const rows = options[kind] || [];
          return (
            <div key={kind} style={styles.panel}>
              <div style={styles.panelTitle}>{title}</div>
              <div style={styles.panelBlurb}>{blurb}</div>

              {rows.length === 0 && <div style={styles.empty}>Nothing yet — this question stays off the floor app.</div>}

              {rows.map((o, i) => (
                <div key={o.id} style={styles.row}>
                  <button
                    style={{ ...styles.iconBtn, opacity: i === 0 || !isAdmin ? 0.35 : 1 }}
                    disabled={i === 0 || busy || !isAdmin}
                    title="Move up"
                    onClick={() => change(o.id, { move: 'up' })}
                  >↑</button>
                  <button
                    style={{ ...styles.iconBtn, opacity: i === rows.length - 1 || !isAdmin ? 0.35 : 1 }}
                    disabled={i === rows.length - 1 || busy || !isAdmin}
                    title="Move down"
                    onClick={() => change(o.id, { move: 'down' })}
                  >↓</button>
                  <span style={o.active ? styles.rowLabel : styles.rowLabelOff}>{o.label}</span>
                  {isAdmin && (
                    <button style={styles.textBtn} disabled={busy} onClick={() => change(o.id, { active: !o.active })}>
                      {o.active ? 'Retire' : 'Bring back'}
                    </button>
                  )}
                </div>
              ))}

              {isAdmin && (
                <>
                  <div style={styles.addRow}>
                    <input
                      ref={el => { inputs.current[kind] = el; }}
                      style={styles.input}
                      value={drafts[kind] || ''}
                      placeholder={placeholder}
                      maxLength={40}
                      onChange={e => setDrafts(prev => ({ ...prev, [kind]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') add(kind); }}
                    />
                    <button style={styles.addBtn} disabled={busy} onClick={() => add(kind)}>Add</button>
                  </div>
                  {errors[kind] && <div style={styles.error}>{errors[kind]}</div>}
                </>
              )}
            </div>
          );
        })}
      </div>

      <div style={styles.note}>
        Retiring a choice takes it off the phone but leaves it on every test that already used it, so old reports
        keep reading the way they did.
        {gameKind && ' Tests recorded before these lists existed keep the single benchmark they have always '
          + 'had — the split starts with the tests you record from now on.'}
      </div>
      {errors.load && <div style={styles.error}>{errors.load}</div>}
    </div>
  );
}

export default FloorOptions;
